import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { parseApiError } from '../../../../utils/errorUtils';
import paymentTypeService, { PaymentType } from '../../../../services/paymentTypeService';
import '../../../../pages/Products/ProductCategoryDeletePage.css';
import { Trash2, Undo2, ArrowLeft } from 'lucide-react';

const PaymentTypeDeletePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [item, setItem] = useState<PaymentType | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { loadItem(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadItem = async () => {
    setLoading(true);
    try {
      const data = await paymentTypeService.getPaymentTypeById(parseInt(id || '0'));
      setItem(data);
    } catch {
      setError(t('paymentType.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!item) return;
    setDeleting(true);
    setError(null);
    try {
      await paymentTypeService.deletePaymentType(item.id);
      navigate('/settings/payment-types', {
        state: { message: t('paymentType.deleteSuccess') },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('paymentType.failedToDelete')).message);
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/settings/payment-types/${item?.id}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="delete-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <div className="delete-container">
          <div className="alert alert-danger">{error || t('paymentType.notFound')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="delete-container">
        {/* Navigation Breadcrumb */}
        <nav className="delete-breadcrumb">
          <button
            className="delete-breadcrumb-btn"
            onClick={handleCancel}
            title={t('paymentType.backToDetail') || item.name}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {item.name}
          </button>
        </nav>

        {/* Page Content */}
        <div className="delete-content">
          {/* Main Delete Card */}
          <div className="delete-card delete-card-main">
            <div className="delete-card-header delete-card-header-warning">
              <Trash2 size={24} strokeWidth={2} className="delete-icon" />
              <h2 className="delete-card-title">{t('paymentType.deleteTitle')}</h2>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="alert alert-danger alert-dismissible fade show">
                {error}
              </div>
            )}

            <div className="delete-card-body">
              <div className="delete-warning-section">
                <p className="delete-warning-text">
                  {t('paymentType.deleteWarning')}
                </p>
              </div>

              {/* Payment Type Details */}
              <div className="delete-category-info">
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('paymentType.code')}</label>
                  <p className="delete-info-value delete-code">{item.code}</p>
                </div>

                <div className="delete-info-item">
                  <label className="delete-info-label">{t('paymentType.name')}</label>
                  <p className="delete-info-value">{item.name}</p>
                </div>

                {item.description && (
                  <div className="delete-info-item delete-info-item-full">
                    <label className="delete-info-label">{t('common.description')}</label>
                    <p className="delete-info-value">{item.description}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="delete-actions">
                <button
                  className="btn-cancel"
                  onClick={handleCancel}
                  disabled={deleting}
                  title={t('common.cancel')}
                >
                  <Undo2 size={16} strokeWidth={1.75} />
                  {t('common.cancel')}
                </button>

                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  disabled={deleting}
                  title={t('common.delete')}
                >
                  {deleting ? (
                    <>
                      <span className="btn-spinner"></span>
                      {t('common.deleting')}...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} strokeWidth={1.75} />
                      {t('paymentType.deleteTitle')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Info Sidebar */}
          <div className="delete-sidebar">
            <div className="delete-card">
              <div className="delete-card-header">
                <h3 className="delete-card-title">{t('paymentType.deleteWarningTitle')}</h3>
              </div>
              <div className="delete-card-body">
                <div className="delete-help-section">
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('common.warning') || 'Permanent Action'}</strong>
                    <p>{t('paymentType.deleteWarning')}</p>
                  </div>
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('common.notes') || 'Note'}</strong>
                    <p>{t('paymentType.deleteConfirmMessage')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PaymentTypeDeletePage;
